import apiClient from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import {
  Review,
  ReviewStatus,
  ReviewComment,
  ApiResponse,
} from '@/types';

// ============================================================================
// REVIEW SERVICE
// API service layer for review-related operations
// ============================================================================

export const reviewService = {
  // =========================================================================
  // Review Operations
  // =========================================================================

  /**
   * Get all reviews with optional filters
   */
  async getReviews(params?: {
    type?: string;
    status?: ReviewStatus;
    reviewerId?: string;
    revieweeId?: string;
  }): Promise<ApiResponse<Review[]>> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.LIST, { params });
    return response.data;
  },

  /**
   * Get a single review by ID
   */
  async getReview(reviewId: string): Promise<ApiResponse<Review>> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.GET(reviewId));
    return response.data;
  },

  /**
   * Create a new review
   */
  async createReview(reviewData: Partial<Review>): Promise<ApiResponse<Review>> {
    const response = await apiClient.post(API_ENDPOINTS.REVIEWS.CREATE, reviewData);
    return response.data;
  },

  /**
   * Get review for a specific task
   */
  async getTaskReview(taskId: string): Promise<ApiResponse<Review>> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.TASK_REVIEW(taskId));
    return response.data;
  },

  /**
   * Submit task for review
   */
  async submitTaskForReview(taskId: string): Promise<ApiResponse<Review>> {
    const response = await apiClient.post(
      API_ENDPOINTS.REVIEWS.SUBMIT_TASK_REVIEW(taskId)
    );
    return response.data;
  },

  // =========================================================================
  // Review Actions
  // =========================================================================

  /**
   * Approve a review
   */
  async approveReview(
    reviewId: string,
    comment?: string
  ): Promise<ApiResponse<Review>> {
    const response = await apiClient.post(
      API_ENDPOINTS.REVIEWS.APPROVE(reviewId),
      { comment }
    );
    return response.data;
  },

  /**
   * Request rework on a review
   */
  async requestRework(
    reviewId: string,
    comment: string
  ): Promise<ApiResponse<Review>> {
    const response = await apiClient.post(
      API_ENDPOINTS.REVIEWS.REQUEST_REWORK(reviewId),
      { comment }
    );
    return response.data;
  },

  /**
   * Reject a review
   */
  async rejectReview(
    reviewId: string,
    comment: string
  ): Promise<ApiResponse<Review>> {
    const response = await apiClient.post(
      API_ENDPOINTS.REVIEWS.REJECT(reviewId),
      { comment }
    );
    return response.data;
  },

  // =========================================================================
  // Comments
  // =========================================================================

  /**
   * Get review comments
   */
  async getComments(reviewId: string): Promise<ApiResponse<ReviewComment[]>> {
    const response = await apiClient.get(
      API_ENDPOINTS.REVIEWS.COMMENTS(reviewId)
    );
    return response.data;
  },

  /**
   * Add a comment to a review
   */
  async addComment(
    reviewId: string,
    content: string,
    mentions?: string[]
  ): Promise<ApiResponse<ReviewComment>> {
    const response = await apiClient.post(
      API_ENDPOINTS.REVIEWS.ADD_COMMENT(reviewId),
      { content, mentions }
    );
    return response.data;
  },

  // =========================================================================
  // Queues
  // =========================================================================

  /**
   * Get my pending reviews (as reviewer)
   */
  async getMyPendingReviews(): Promise<ApiResponse<Review[]>> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.MY_PENDING);
    return response.data;
  },

  /**
   * Get team pending reviews
   */
  async getTeamPendingReviews(): Promise<ApiResponse<Review[]>> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.TEAM_PENDING);
    return response.data;
  },
};

export default reviewService;
